import { useMount } from 'ahooks';
import { Modal } from 'antd';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNullOrWhiteSpaces } from '../utils/strings';

// ref: https://www.cookiebot.com/en/developer/

export enum CookiebotEvents {
  CookiebotOnConsentReady = 'CookiebotOnConsentReady',
  CookiebotOnLoad = 'CookiebotOnLoad',
  CookiebotOnAccept = 'CookiebotOnAccept',
  CookiebotOnDecline = 'CookiebotOnDecline',
  CookiebotOnDialogInit = 'CookiebotOnDialogInit',
  CookiebotOnDialogDisplay = 'CookiebotOnDialogDisplay',
  CookiebotOnTagsExecuted = 'CookiebotOnTagsExecuted'
}

export interface CookiebotDOM {
  consent: {
    necessary: boolean;
    preferences: boolean;
    statistics: boolean;
    marketing: boolean;
    method: 'implied' | 'explicit';
  };
  consented: boolean;
  declined: boolean;
  hasResponse: boolean;
  doNotTrack: boolean;
  regulations: {
    gdprApplies: boolean;
    ccpaApplies: boolean;
    lgpdApplies: boolean;
  };

  show: () => void;
  hide: () => void;
  renew: () => void;
  getScript: (url: string, async: boolean, callback: () => void) => void;
  runScripts: () => void;
  withdraw: () => void;
  submitCustomConsent: (optinPreferences: boolean, optinStatistics: boolean, optinMarketing: boolean) => void;
}

declare global {
  interface Window {
    Cookiebot: CookiebotDOM;
  }
}

export interface CookiebotProviderProps extends PropsWithChildren {
  active?: boolean;
  cbid: string;
  blockingMode?: null | undefined | 'auto' | 'none';
  useRejectNeededCookiesFlow?: boolean;
  scriptId?: string;
}

const CookiebotProvider: FC<CookiebotProviderProps> = ({
  active,
  cbid,
  blockingMode,
  scriptId = 'Cookiebot',
  useRejectNeededCookiesFlow,
  children
}) => {
  const { t } = useTranslation();

  useMount(() => {
    if (!active || isNullOrWhiteSpaces(cbid)) return;

    const src = 'https://consent.cookiebot.com/uc.js';
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].getAttribute('src') === src || scripts[i].id === scriptId) {
        return; // Script already exists
      }
    }

    // Create script component.
    const script = document.createElement('script');
    script.src = src;
    script.id = scriptId;
    script.setAttribute('data-cbid', cbid);
    if (!isNullOrWhiteSpaces(blockingMode)) script.setAttribute('data-blockingmode', blockingMode!);

    document.body.appendChild(script);
  });

  useEffect(() => {
    const f = () => {
      if (!useRejectNeededCookiesFlow) return;

      document.cookie = 'CookieConsent=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      Modal.confirm({
        title: <span style={{ fontFamily: 'OrkneyRegular' }}>{t('Rechazaste las cookies necesarias')}</span>,
        content: (
          <span style={{ fontFamily: 'OrkneyRegular' }}>
            {t(
              'Si rechazas las cookies necesarias el sitio no puede funcionar por lo que no podrás acceder a jobs2live. ¿Deseas continuar con tu decisión?'
            )}
          </span>
        ),
        onOk: () => {
          window.location.href = 'https://www.google.com';
        },
        onCancel: () => {
          window.Cookiebot.renew();
        },
        okButtonProps: {
          shape: 'round',
          type: 'primary',
          style: { fontFamily: 'OrkneyMedium', paddingLeft: 22, paddingRight: 22, height: 32, backgroundColor: '#22817a' }
        },
        cancelButtonProps: {
          shape: 'round',
          style: {
            borderColor: '#22817a',
            color: '#1A1C21',
            fontFamily: 'OrkneyMedium',
            paddingLeft: 22,
            paddingRight: 22,
            height: 32,
            paddingTop: 0,
            paddingBottom: 0
          }
        },
        okText: t('Sí, salir'),
        cancelText: t('No, actualizar consentimiento')
      });
    };

    window.addEventListener(CookiebotEvents.CookiebotOnDecline, f);
    return () => {
      window.removeEventListener(CookiebotEvents.CookiebotOnDecline, f);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
};

export const useCookiebot = () => {
  const [cb, setCb] = useState(window.Cookiebot);

  useEffect(() => {
    const f = () => {
      setCb(window.Cookiebot);
    };
    window.addEventListener(CookiebotEvents.CookiebotOnLoad, f);
    return () => {
      window.removeEventListener(CookiebotEvents.CookiebotOnLoad, f);
    };
  }, []);

  return { ready: cb != null, cookiebot: cb };
};

export default CookiebotProvider;
